import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, fonts, borderRadius, spacing } from '../../../constants';

/**
 * TopServicesList Component
 * Displays a list of top services with values
 * 
 * @param {Array<Object>} services - Array of service objects with name and value
 */
const TopServicesList = ({ services }) => {
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        backdropFilter: 'blur(16px) saturate(180%)',
        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        borderRadius: borderRadius.lg,
        padding: spacing.sm,
        border: '1px solid rgba(255, 255, 255, 0.25)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'slideInLeft 0.6s ease-out 0.5s both',
        '@keyframes slideInLeft': {
          from: {
            opacity: 0,
            transform: 'translateX(-30px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
          transform: 'translateY(-2px)',
          border: '1px solid rgba(255, 255, 255, 0.35)',
        },
      }}
    >
      <Typography
        sx={{
          fontSize: fonts.fontSizeSm,
          fontWeight: fonts.weightSemiBold,
          color: colors.textWhite,
          mb: spacing.xs,
          textAlign: 'left',
          textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        }}
      >
        Top Services
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {services.map((service, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 8px',
              borderRadius: borderRadius.sm,
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: `fadeIn 0.4s ease-out ${0.7 + index * 0.1}s both`,
              '@keyframes fadeIn': {
                from: {
                  opacity: 0,
                  transform: 'translateX(-10px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transform: 'translateX(4px)',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: fonts.fontSizeSm,
                color: colors.textWhite,
                fontWeight: fonts.weightMedium,
                textAlign: 'left',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
              }}
            >
              {service.name}
            </Typography>
            <Typography
              sx={{
                fontSize: fonts.fontSizeSm,
                fontWeight: fonts.weightBold,
                color: colors.textWhite,
                textAlign: 'right',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
              }}
            >
              {service.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TopServicesList;
